import { RequestStatus } from "@constants/repositories";
import { IRootTreeModel } from "@models/index";
import { Logging } from "@services/logging";
import {
  IListViewModel,
  IListViewModelFilters,
  IListViewModelSorter,
} from "@viewModels/ListViewModel/__types__/IListViewModel";
import { IViewModel } from "@viewModels/__types__/IViewModel.types";
import { isNumber } from "lodash";
import { IObservableValue, toJS } from "mobx";
import moment from "moment";

/**
 * Type definition for synchronizer object.
 */
interface TSynchronizer {
  handler: IListViewModel<IListViewModelSorter, IListViewModelFilters>;
  intervalToUpdate: number; // Interval in minutes for synchronization
  status: IObservableValue<RequestStatus>; // Observable value for synchronization status
  name: string; // Name of the synchronizer
}

/**
 * Class responsible for handling synchronization with storage.
 */
export class StorageSyncViewModel implements IViewModel {
  private timeout: null | NodeJS.Timeout | number = null;

  /**
   * Constructor accepting model and array of synchronizers.
   *
   * @param model The root model.
   * @param synchronizers Array of synchronizers.
   */
  public constructor(
    private model: IRootTreeModel,
    private synchronizers: TSynchronizer[]
  ) {}

  /**
   * Method to schedule synchronization.
   * Schedules synchronization for each synchronizer and sets a timeout for the next synchronization.
   *
   * @returns A promise that resolves when all synchronizations are scheduled.
   */
  public scheduleSyncs = async (): Promise<void> => {
    if (this.timeout) clearTimeout(this.timeout);
    await Promise.all(
      this.synchronizers.map(
        async (synchronizer) => await this.scheduleSynchronization(synchronizer)
      )
    );
    this.timeout = setTimeout(this.scheduleSyncs, 60 * 1000);
  };

  /**
   * Method called before destroying the instance.
   * Calls beforeDestroy for each synchronizer handler.
   */
  public beforeDestroy = (): void => {
    this.synchronizers.forEach((synchronizer) =>
      synchronizer.handler.beforeDestroy()
    );
  };

  /**
   * Method to schedule synchronization for a specific synchronizer.
   * Checks if it's time to synchronize and performs synchronization if needed.
   *
   * @param synchronizer The synchronizer to schedule synchronization for.
   * @returns A promise that resolves when synchronization is scheduled.
   */
  private scheduleSynchronization = async (
    synchronizer: TSynchronizer
  ): Promise<void> => {
    const lastSync = this.model.storage.lastSynchronization.get(
      synchronizer.name
    );
    const diff = lastSync
      ? moment().diff(moment(lastSync?.timestamp), "minutes")
      : null;

    Logging.info("Storage sync", {
      current_diff: diff,
      entity: synchronizer.name,
      lastSync: lastSync
        ? moment(lastSync.timestamp).format("HH:mm DD/MM/YY")
        : null,
      limit_diff: synchronizer.intervalToUpdate,
      status: toJS(synchronizer.status.get()),
    });

    if (synchronizer.status.get() === RequestStatus.Pending) return;

    if (isNumber(diff)) {
      if (diff < synchronizer.intervalToUpdate) {
        return;
      }
    }

    await this.handleSynchronization(synchronizer);

    this.model.storage.setLastSyncDate(synchronizer.name, Date.now());
  };

  /**
   * Method to handle synchronization for a specific synchronizer.
   * Iterates through pages and fetches items, updating synchronization status accordingly.
   *
   * @param synchronizer The synchronizer to handle synchronization for.
   * @returns A promise that resolves when synchronization is handled.
   */
  private handleSynchronization = async (
    synchronizer: TSynchronizer
  ): Promise<void> => {
    try {
      synchronizer.status.set(RequestStatus.Pending);
      let i = 0;
      while (i < synchronizer.handler.meta.last_page) {
        i++;
        synchronizer.handler.setPagination({ current_page: i });
        await synchronizer.handler.fetchListOfItems();
      }

      Logging.success("Storage sync", {
        entity: synchronizer.name,
        status: RequestStatus.Success,
      });

      synchronizer.status.set(RequestStatus.Success);
    } catch (error) {
      Logging.error("Storage sync", {
        entity: synchronizer.name,
        status: RequestStatus.Error,
      });

      synchronizer.status.set(RequestStatus.Error);
      // throw error;
    }
  };
}
